import { useMemo, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../hooks/useAuth';
import { useToast } from '../../common/ToastProvider';
import { isAdvertiserAccount } from './navItems';

/**
 * «أضف عقارك»: where the list-your-property action goes for this viewer, and the
 * licence checks that run before it does. Shared by the Services menu and the
 * sidebar's full-width CTA, so both behave identically.
 *
 * - Guest: /signin, then on to the FAL disclaimer that starts the owner flow.
 * - Broker / company: the listing form, but only with an APPROVED FAL licence.
 *   A rejected or pending licence blocks the click with a toast instead.
 * - Anyone else: the owner's property request (listed under Safaqat 24's licence).
 *
 * Render it as a router <Link to={to} state={state} onClick={…}>: the click
 * handler calls preventDefault() to block, which <Link> respects.
 */

export interface ListPropertyAction {
  to: string;
  /** Router state for the link (the post-sign-in destination, for guests). */
  state?: { from: { pathname: string } };
  /**
   * Run on click. `after` always runs (close the menu or drawer), whether the
   * navigation goes ahead or is blocked.
   */
  onClick: (event: MouseEvent<HTMLElement>, after?: () => void) => void;
}

export function useListPropertyAction(): ListPropertyAction {
  const { user } = useAuth();
  const { showToast } = useToast();
  const { t } = useTranslation('nav');

  return useMemo<ListPropertyAction>(() => {
    if (!user) {
      return {
        to: '/signin',
        state: { from: { pathname: '/fal-disclaimer' } },
        onClick: (_event, after) => after?.(),
      };
    }

    const advertiser = isAdvertiserAccount(user);
    return {
      to: advertiser ? '/properties/new' : '/individual-property-request',
      onClick: (event, after) => {
        if (advertiser) {
          const falStatus = user.falVerificationStatus;
          if (falStatus === 'REJECTED') {
            event.preventDefault();
            showToast(t('listProperty.falRejected'), 'error');
          } else if (falStatus !== 'APPROVED') {
            event.preventDefault();
            showToast(t('listProperty.falPending'), 'info');
          }
        }
        after?.();
      },
    };
  }, [user, showToast, t]);
}

export default useListPropertyAction;
